import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

function getRetryStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getRetryKey(key: string) {
  return `maddox:lazy-retry:${key}`;
}

function isChunkLoadError(error: unknown) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function shouldReloadForChunkError(key: string) {
  const storage = getRetryStorage();
  if (!storage) return false;

  const retryKey = getRetryKey(key);
  if (storage.getItem(retryKey) === "1") {
    return false;
  }

  storage.setItem(retryKey, "1");
  return true;
}

function clearRetryMarker(key: string) {
  getRetryStorage()?.removeItem(getRetryKey(key));
}

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  key: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await importer();
      clearRetryMarker(key);
      return module;
    } catch (error) {
      if (
        typeof window !== "undefined" &&
        isChunkLoadError(error) &&
        shouldReloadForChunkError(key)
      ) {
        // A stale client can reference chunks from the previous deploy hash.
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }

      throw error;
    }
  });
}
